import { Condition, Procedure, QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  ProceduresAmendmentBoundaries,
  ProceduresAmendmentVersionPair,
  ProceduresAmendmentWindow,
} from "../types/procedures";

import { parseIsoInstant } from "./questionnaire-response-instant";

/**
 * Resolves the window one amendment's procedure writes fall in.
 *
 * The encounter's own QuestionnaireResponse version is written before the sections save, so an
 * amendment's procedure writes land after the version being viewed and before the next one. The next
 * newer version closes the window; there is nothing to close it with when the amendment being viewed
 * is the latest, and the encounter as it stands is what that amendment left behind.
 *
 * @param amendmentHistory - The encounter QuestionnaireResponse's versions, newest first.
 * @param versionId - The amendment version being viewed.
 * @returns The window, or undefined when the version is not in the history or carries no timestamp.
 */
export const resolveProceduresAmendmentWindow = (
  amendmentHistory: QuestionnaireResponse[] | undefined,
  versionId: string | undefined,
): ProceduresAmendmentWindow | undefined => {
  if (!amendmentHistory?.length || !versionId) return undefined;

  const viewedIndex = amendmentHistory.findIndex(
    (questionnaireResponse) => questionnaireResponse.meta?.versionId === versionId,
  );
  if (viewedIndex === -1) return undefined;

  const openedAt = amendmentHistory[viewedIndex].meta?.lastUpdated;
  if (!openedAt) return undefined;

  return { openedAt, closedAt: amendmentHistory[viewedIndex - 1]?.meta?.lastUpdated };
};

/**
 * Parses a window's timestamps once, so every version chain is compared against the same instants.
 *
 * Either end failing to parse gives up on the whole window. An absent `closedAt` means the viewed
 * amendment is the latest one, so a closing timestamp that is present but unparseable cannot be
 * dropped: the diff would read the window as open and show later amendments' writes as this
 * amendment's.
 *
 * @param window - The window to parse.
 * @returns The boundaries as epoch milliseconds, or undefined when either end carries an
 *   unparseable timestamp.
 */
export const toProceduresAmendmentBoundaries = (
  window: ProceduresAmendmentWindow,
): ProceduresAmendmentBoundaries | undefined => {
  const openedAt = parseIsoInstant(window.openedAt);
  if (openedAt === undefined) return undefined;

  if (window.closedAt === undefined) return { openedAt, closedAt: undefined };

  const closedAt = parseIsoInstant(window.closedAt);
  if (closedAt === undefined) return undefined;

  return { openedAt, closedAt };
};

/** Newest version first, keeping the server's order when two versions share an instant. */
const toNewestFirst = <ResourceVersion extends Procedure | Condition>(
  versions: ResourceVersion[],
): ResourceVersion[] =>
  [...versions].sort(
    (first, second) =>
      (parseIsoInstant(second.meta?.lastUpdated) ?? 0) -
      (parseIsoInstant(first.meta?.lastUpdated) ?? 0),
  );

/**
 * The newest version stored before `instant`, or undefined when the resource is younger.
 *
 * Both boundaries exclude the instant itself, which makes an amendment's window the half-open range
 * from its own signature up to the next one's. A version written in the same millisecond as a
 * signature belongs to the amendment that signature opens, because Sign & Lock writes the
 * encounter's QuestionnaireResponse version first and the sections' resources after it. Comparing
 * inclusively would put such a version on both sides of the same signature, so the amendment that
 * wrote it would show nothing changed and the amendment before it would claim the change as its
 * own.
 */
const pickVersionBefore = <ResourceVersion extends Procedure | Condition>(
  versionsNewestFirst: ResourceVersion[],
  instant: number,
): ResourceVersion | undefined =>
  versionsNewestFirst.find((version) => {
    const storedAt = parseIsoInstant(version.meta?.lastUpdated);
    return storedAt !== undefined && storedAt < instant;
  });

/**
 * Picks the version of one resource each side of the amendment reads.
 *
 * The versions are re-ordered here rather than taken as the read returned them: the pair decides what
 * the amendment view claims changed, so it does not rest on the server's bundle order.
 *
 * @param versions - Every stored version of one procedure or diagnosis, in any order.
 * @param boundaries - The amendment's parsed boundaries.
 * @returns The version current when the amendment opened, and the one it left behind. Either side is
 *   undefined when the resource did not exist yet at that point.
 */
export const pickProceduresAmendmentVersionPair = <ResourceVersion extends Procedure | Condition>(
  versions: ResourceVersion[],
  boundaries: ProceduresAmendmentBoundaries,
): ProceduresAmendmentVersionPair<ResourceVersion> => {
  const versionsNewestFirst = toNewestFirst(versions);
  const { openedAt, closedAt } = boundaries;

  return {
    previous: pickVersionBefore(versionsNewestFirst, openedAt),
    // An open window means the viewed amendment is the latest one, so the amended side is simply the
    // resource as it stands.
    current:
      closedAt === undefined
        ? versionsNewestFirst[0]
        : pickVersionBefore(versionsNewestFirst, closedAt),
  };
};
